import { FilterPreset, FilterSchema } from '../../../molecules/FilterBuilder/types';
import { shiftDaysISO, todayISO } from '../../../../utils/DateUtils';

// Serialização reaproveitada da tela de títulos (mesmo formato filter[campo][operador])
export { serializeFilters } from '../../settlement/constants/filterSchemas';

/** Natureza do título por trás da fatura — mesmos rótulos e cores da tela de Títulos. */
export { CHARGE_SCENARIO_LABELS as BUSINESS_REFERENCE_LABELS } from './chargeFilterSchema';

/**
 * Atalhos de vencimento — as faixas que a cobrança realmente usa para decidir o que fazer com um
 * título (régua de cobrança, protesto, perda esperada).
 *
 * As janelas de vencido são CONTÍGUAS e não se sobrepõem: a de "até 30" termina em ontem, e cada
 * uma seguinte começa um dia antes do início da anterior. Sem isso um título de exatamente 30 dias
 * cairia em duas faixas e a soma das faixas não bateria com o total.
 *
 * A conta é sempre em cima de HOJE, avaliada no clique — ver o comentário de `FilterPreset`.
 */
const janela = (label: string, hint: string, de: number, ate: number): FilterPreset => ({
    label, hint,
    apply: () => ({ operator: 'between_dates', value: [shiftDaysISO(de), shiftDaysISO(ate)] }),
});

export const DUE_DATE_PRESETS: FilterPreset[] = [
    {
        label: 'Vence hoje', hint: 'Vencimento igual a hoje',
        apply: () => ({ operator: 'between_dates', value: [todayISO(), todayISO()] }),
    },
    janela('Vence em 30 dias', 'De hoje até 30 dias à frente', 0, 30),
    // A primeira faixa nasceu como "até 30" e foi partida em duas: o começo do atraso é onde a
    // régua de cobrança age, e trinta dias num balde só escondiam a diferença entre quem venceu
    // ontem e quem venceu há um mês.
    janela('Vencido 15 dias -', 'Vencido há até 15 dias', -15, -1),
    janela('Vencido 15-30 dias', 'Vencido há mais de 15 e até 30 dias', -30, -16),
    janela('Vencido 30-60 dias', 'Vencido há mais de 30 e até 60 dias', -60, -31),
    janela('Vencido 60-90 dias', 'Vencido há mais de 60 e até 90 dias', -90, -61),
    {
        label: 'Vencido 90 dias +', hint: 'Vencido há mais de 90 dias',
        // `before` serializa como `lte` (inclusivo), daí o -91: com -90 esta faixa engoliria o
        // último dia da anterior.
        apply: () => ({ operator: 'before', value: shiftDaysISO(-91) }),
    },
];

export const invoiceFilterSchema: FilterSchema[] = [
    // ─── Campos Principais (visíveis) ─────────────────────────────────────────
    // Só os três que a tela abre pré-preenchidos. O resto entra por "+ Filtro" — painel curto
    // é painel que se lê de relance, e ele nasce colapsado.
    { key: 'status', label: 'Status', type: 'enum', operators: ['in'], defaultOperator: 'in', visible: true, options: [
        { value: 'OPEN', label: 'Em aberto' },
        { value: 'PAID', label: 'Pago' },
        { value: 'CANCELLED', label: 'Cancelado' },
        // PARTIALLY_PAID existe no enum do domínio mas NUNCA é gravado: pagamento parcial liquida
        // a fatura e deriva outra para o resto (ver derive_invoices). Oferecer o filtro só
        // devolvia lista vazia.
    ]},
    { key: 'due_date', label: 'Vencimento', type: 'date', operators: ['before', 'after', 'between_dates'], defaultOperator: 'between_dates', visible: true, presets: DUE_DATE_PRESETS },
    { key: 'collect', label: 'Em cobrança', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: true },
    // Estados do TÍTULO (AF-3674): a cobrança saiu das nossas mãos. Ocultos porque são busca de
    // exceção — quem procura sabe o que procura.
    { key: 'subrogation', label: 'Sub-rogado', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'judicial_petition', label: 'Jurídico', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },

    // ─── Campos Ocultos (aparecem via "+ Filtro") ─────────────────────────────
    // contrato e natureza vêm do TÍTULO (settlement_v2.title_debt), não da fatura — o backend
    // resolve com EXISTS correlacionado por (operação, charge)
    { key: 'contract_number', label: 'Nº Contrato', type: 'text', operators: ['contains', 'eq'], defaultOperator: 'contains', visible: false },
    { key: 'business_reference', label: 'Natureza', type: 'enum', operators: ['in'], defaultOperator: 'in', visible: false, options: [
        { value: 'RECEIVABLE', label: 'Recebível' },
        { value: 'INSTALLMENT', label: 'Parcela' },
        { value: 'GUARANTEE', label: 'Garantia' },
    ]},
    // EMITENTE / CEDENTE: quem ORIGINOU a dívida — o participante primário da operação, eleito
    // pelo LATERAL `elected_issuer`. Não confundir com o Devedor logo abaixo, que é quem paga.
    { key: 'issuer_name', label: 'Emitente/Cedente', type: 'text', operators: ['contains'], defaultOperator: 'contains', visible: false },
    { key: 'issuer_document', label: 'Documento Emitente/Cedente', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
    // ⚠️ A CHAVE continua `payer_*` (é o nome no servidor); só o RÓTULO virou "Devedor" (AF-3763).
    { key: 'payer_name', label: 'Devedor', type: 'text', operators: ['contains', 'eq'], defaultOperator: 'contains', visible: false },
    { key: 'overdue', label: 'Vencidos (em aberto)', type: 'boolean', operators: ['is_true'], defaultOperator: 'is_true', visible: false },
    { key: 'amount', label: 'Valor', type: 'number', operators: ['gte', 'lte', 'between'], defaultOperator: 'between', visible: false },
    { key: 'payment_method', label: 'Meio Pagamento', type: 'enum', operators: ['in'], defaultOperator: 'in', visible: false, options: [
        { value: 'BOLETO', label: 'Boleto' },
        { value: 'PIX', label: 'PIX' },
        { value: 'TED', label: 'TED' },
        { value: 'ESCROW', label: 'Conta Escrow' },
    ]},
    { key: 'payment_type', label: 'Tipo Pagamento', type: 'enum', operators: ['in'], defaultOperator: 'in', visible: false, options: [
        { value: 'FINANCIAL', label: 'Financeiro' },
        { value: 'DEBT_CONFESSION', label: 'Confissão de Dívida' },
        { value: 'ASSET_DATION', label: 'Dação em Pagamento' },
    ]},
    { key: 'has_boleto', label: 'Com boleto', type: 'boolean', operators: ['is_true', 'is_false'], defaultOperator: 'is_true', visible: false },
    { key: 'document_title', label: 'Título/NF', type: 'text', operators: ['contains', 'eq'], defaultOperator: 'contains', visible: false },
    { key: 'payer_document', label: 'Documento Devedor', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
    { key: 'invoice_id', label: 'Fatura (id)', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
    { key: 'charge_id', label: 'Recebível (charge_id)', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
    { key: 'credit_operation_id', label: 'Operação (id)', type: 'text', operators: ['eq'], defaultOperator: 'eq', visible: false },
];
